import { useEffect, useState, useCallback } from 'react';
import type { WordTimestamp } from '../types';

type MediaRef = React.MutableRefObject<HTMLMediaElement | null> | React.RefObject<HTMLMediaElement | null>;

export default function useMediaSync(mediaRef: MediaRef | null, wordTimestamps: WordTimestamp[] | null, active = true) {
  const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [playing, setPlaying] = useState<boolean>(false);

  const seekTo = useCallback((s?: number) => {
    if (typeof s !== 'number' || Number.isNaN(s)) return;
    try {
      const el = mediaRef && 'current' in mediaRef ? mediaRef.current : null;
      if (!el) return;
      el.currentTime = Math.max(0, s);
    } catch (e) {
      console.error('Error seeking media element:', e);}
  }, [mediaRef]);

  useEffect(() => {
    if (!mediaRef || !('current' in mediaRef)) return;
    const el = mediaRef.current;
    if (!el || !active || !wordTimestamps || !Array.isArray(wordTimestamps)) return;

    const onTime = () => {
      const t = el.currentTime || 0;
      setCurrentTime(t);
      let idx: number | null = null;
      for (let k = wordTimestamps.length - 1; k >= 0; k--) {
        const w = wordTimestamps[k];
        const start = Number(w.start) || 0;
        if (t >= start) {
          idx = k;
          break;
        }
      }
      setSelectedWordIndex(idx);
    };

    const onDuration = () => setDuration(el.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    el.addEventListener('timeupdate', onTime);
    el.addEventListener('durationchange', onDuration);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);

    setCurrentTime(el.currentTime || 0);
    setDuration(el.duration || 0);
    setPlaying(!el.paused);

    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('durationchange', onDuration);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, [mediaRef, wordTimestamps, active]);

  const play = useCallback(() => {
    const el = mediaRef && 'current' in mediaRef ? mediaRef.current : null;
    if (!el) return Promise.resolve();
    return el.play().catch(() => undefined);
  }, [mediaRef]);

  const pause = useCallback(() => {
    const el = mediaRef && 'current' in mediaRef ? mediaRef.current : null;
    if (!el) return;
    el.pause();
  }, [mediaRef]);

  return {
    selectedWordIndex,
    setSelectedWordIndex,
    seekTo,
    play,
    pause,
    currentTime,
    duration,
    playing,
  };
}
